export enum userRoles {
  ADMIN,
  DEFAULT_USER,
  SUBUSER,
}

export enum guestStatus {
  TO_FORM,
  TO_VALIDATE,
  TO_APROVE,
  ENDED,
  FILLING,
  REFUSED,
  CANCELED,
  CHECKOUT_FINISHED
}

export enum GuestRating {
  VERY_GOOD = "5",
  GOOD = "4",
  REGULAR = "3",
  BAD = "2",
  VERY_BAD = "1",
}

export enum OptionChoice {
  YES = "1",
  NO = "0",
}

export enum DocType {
  BACK = "doc_back",
  FRONT = "doc_front",
  SELFIE = "doc_selfie",
  DIGITAL = "doc_digital",
}

export interface fieldsForFormPDFField {
  officeText: string;
  contactText: string;
  bottomText: string;
  observationsAndRulesText?: string;
}

export interface IUser {
  id: string;
  email: string;
  name: string;
  password: string;
  config: IUserConfig;
  telefone: string;
  name_enterprise?: string;
  logo?: string;
  cnpj: string;
  image: string;
  role: userRoles;
  thankYouMessages: Record<string, string>;
  thankYouMessagesAdditionalGuest: Record<string, string>;
  cautionExplanationURL: string;
  gpdlSensitiveDataUrl: string;
  subscriptions: Subscription[];
  currentSubscription: Subscription;
  fieldsForFormPDF: fieldsForFormPDFField;
  friendlyCode?: string;
  deleted_at?: string | null;
  reservationDelegationEnabled: OptionChoice;
  supportedLanguages: LangType[];
  sourcesConfig?: SourcesConfig;
  limitations: UserLimitations;
  contactsData?: ContactsData;
  filterOptions?: userFilterOptions;
  showTermsOfUseOnGuestForm?: string;
  showMovidaAvertsimentOnGuestForm?: string;
  termsOfUse?: string;
  companyName?: string;
}

export interface UserLimitations {
    staysConnectionDisabled?: OptionChoice;
    uniqueFormLink?: OptionChoice;
}

export enum Partner {
  STAYS = "stays",
  /**
   * Reservas criadas manualmente
   */
  MYSELF = 'myself',
}

export interface IntervalInterface {
  checkin: {
    start: string;
    end: string;
  };
  checkout: {
    start: string;
    end: string;
  };
}

export interface PartnerCodesInterface {
  [Partner.STAYS]?: string;
  [Partner.MYSELF]?: string;
}
export interface NamesInterface {
  pt_BR: string;
  en_US: string;
  es_ES: string;
}

export interface residenceInfoInterface {
  pt_BR: string;
  en_US: string;
  es_ES: string;
}

export interface TtLockAccount {
  id?: number;
  name: string;
  user: string;
  secret: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface IUserConfig {
  id: number;
  staysClientURL: string;
  staysClientUsername: string;
  staysClientPassword: string;
  staysClientReferenceDateTime: string;
  pixKey: string;
  serproClientId: string;
  serproClientSecret: string;
  serproClientToken: string;
  ttlock_accounts: TtLockAccount[];
  socialMediaLinks: UserSocialMediaLinks;
  pagarmeConnected?: boolean;
  pagarmeSecretKey?: string;
  pagarmePublicKey?: string;
}

export enum ReservationSource {
  STAYS = "stays",
  GUESTY = "guesty",
  AIRBNB = "airbnb",
  EXPEDIA = "expedia",
  BOOKING = "booking",
  HOTEIS_COM = "hoteis_com",
  TRIVAGO = "trivago",
  TRIPADVISOR = "tripadvisor",
  VRBO = "vrbo",
  DECOLAR = "decolar",
  SUBMARINO_VIAGENS = "submarino_viagens",
  OTHER = "other",
}

export const ReservationSourceNames: Record<ReservationSource, string> = {
  [ReservationSource.STAYS]: "Stays",
  [ReservationSource.GUESTY]: "Guesty",
  [ReservationSource.AIRBNB]: "AirBnB",
  [ReservationSource.EXPEDIA]: "Expedia",
  [ReservationSource.BOOKING]: "Booking.com",
  [ReservationSource.HOTEIS_COM]: "Hotéis.com",
  [ReservationSource.TRIVAGO]: "Trivago",
  [ReservationSource.TRIPADVISOR]: "Tripadvisor",
  [ReservationSource.VRBO]: "VRBO",
  [ReservationSource.DECOLAR]: "Decolar.com",
  [ReservationSource.SUBMARINO_VIAGENS]: "Submarino Viagens",
  [ReservationSource.OTHER]: "Outros",
};

export type ReservationIds = Partial<Record<ReservationSource, string | null>>;

export interface IntervalInterface {
  checkin: {
    start: string;
    end: string;
  };
  checkout: {
    start: string;
    end: string;
  };
}

export interface Party {
  Name?: string | null;
  SpecificType?: string | null;
}

export interface ProcessData {
  cpf?: string | null;
  Number?: string | null;
  Type?: string | null;
  MainSubject: string;
  Status?: string | null;
  CourtName?: string | null;
  CourtLevel?: string | null;
  CourtType?: string | null;
  Judge?: string | null;
  Value?: number | null;
  CloseDate?: string | null;
  LastMovementDate?: string | null;
  Parties?: Party[] | null;
}

export interface ProcessDataField {
  cpf: string;
  processes: ProcessData[];
}

export interface UserSocialMediaLinks {
  instagram?: string;
}

export interface AdditionalGuest {
  name?: string;
  cpf?: string;
  document?: string;
  phone?: string;
  photo?: File;
  photo_url?: string;
  photo_back?: File;
  photo_back_url?: string;
  is_foreign?: boolean;
  isForeignAd?: number; // TODO verificar a real necessidade deste campo, pois em alguns lugares está usando assim
  is_child?: boolean;
  doc_selfie?: string;
  serproValidated?: OptionChoice;
  serproSimilarity?: number;
  processesData?: ProcessDataField;
  birthDate?: Date
}

export type AdditionalGuestsMap = Record<number, AdditionalGuest>;

export enum CautionPaymentMethod {
  CREDIT_CARD = 'credit_card',
  PIX = 'pix',
  INSURANCE = 'insurance',
}

// CautionCreditCardData removed — PCI-DSS SAQ A compliance (card data handled via Pagar.me iframe)

export interface Guest {
  isCautionBlocked?: boolean;
  isLongStay?: boolean;
  isCreditCardAllowed?: boolean;
  id: number;
  partner: Partner;
  source: ReservationSource;
  ids: ReservationIds;
  extraData: Record<string, string>;
  sourceName: string;
  sourceImg: string;
  ownerLogo: string;
  name: string;
  is_foreign: "1" | "0" | null | undefined;
  hasCaution: "2" | "1" | "0" | null | undefined;
  serproValidated: "1" | "0";
  serproSimilarity: number;
  cpf: string;
  document: string;
  email: string;
  phone: string;
  chekin: string;
  status: guestStatus;
  checkout: string;
  docType: "physical" | "digital" | null | undefined;
  docs: Record<DocType, string> | null | undefined;
  motive: string;
  refuseMotive: string;
  cancelMotive: string;
  pets: string;
  maxGuests: number;
  residence_id: number;
  address: Partial<Address>;
  documents: [];
  residence: Residence;
  credit_paid: number;
  lang: LangType;
  additionalGuests: AdditionalGuestsMap;
  depositChecked: boolean;
  deposit: number;
  cautionValue: number;
  cautionPayment: string;
  cautionPaymentMethod?: CautionPaymentMethod | null;
  cautionTransactionStatus?: string | null;
  cautionTransactionId?: string | null;
  cautionChargeId?: string | null;
  cautionPreAuthorizedAt?: string | null;
  rating?: GuestRating | null | undefined;
  would_do_business_again?: OptionChoice | null | undefined;
  withVehicle: OptionChoice;
  vehicles: VehiclesDataInterface;
  user: IUser;
  responsibleUser: IUser;
  checkoutResponsibleUser: IUser;
  isCheckInFinished: boolean;
  isCheckoutFinished: boolean;
  responsibleUserId: string | number | null;
  checkoutResponsibleUserId: string | number | null;
  processesData: ProcessDataField;
  observations: string | undefined;
  isReservationHidden?: boolean;
  dynamicPassword?: string | null;
  hasResidenceChanged: boolean;
  socialMediaLinks?: UserSocialMediaLinks;
  created_at?: string;
  gpdlSensitiveDataUrl?: string | null;
  checkInStepsModelsProgress: StepsProgressStatus;
  checkoutStepsModelsProgress: StepsProgressStatus;
  checkInStepsMessage: string;
  checkoutStepsMessage: string;
  hasChildren: boolean;
  thankYouMessages: Record<string, string>;
  thankYouMessagesAdditionalGuest: Record<string, string>;
  pix: string;
  supportedLanguages: LangType[];
  timeChangedOutsideAllowedRange: OptionChoice;
  hasAdditionalGuest?: OptionChoice;
  cautionExplanationURL?: string;
  notificationStatus?: NotificationStatus;
  createdByUniqueFormLink: OptionChoice;
  termsAccepted: OptionChoice;
  userTermsOfUseAccepted?: OptionChoice;
  birthDate?: Date,
  petsData?: petsData;
  checkinTime?: string;
  checkoutTime?: string;
}

export interface Address {
  id: number;
  isForeignAddress: "1" | "0" | null | undefined;
  street: string;
  city: string;
  state: string;
  zip: string;
  number: string;
  obs: string;
  country?: string;
}

export interface CondoReleaseSystem {
  id: number;
  name: string;
}


export enum ResponsibleType {
  DOORMAN,
  CLEANING,
  RESPONSIBLE,
  OWNER,
  OTHER,
}

export interface NotificationActions {
  sawCreatedNotification?: OptionChoice;
  sawApprovedNotification?: OptionChoice;
  sawCanceledNotification?: OptionChoice;
  sawReminderNotification?: OptionChoice;
  receivedCreatedNotification?: OptionChoice;
  receivedApprovedNotification?: OptionChoice;
  receivedCanceledNotification?: OptionChoice;
  receivedReminderNotification?: OptionChoice;
}

export interface ResponsibleNotificationStatus {
  [responsibleName: string]: NotificationActions;
}

export interface NotificationStatus {
  responsibles: {
    [ResponsibleType.DOORMAN]?: ResponsibleNotificationStatus;
    [ResponsibleType.CLEANING]?: ResponsibleNotificationStatus;
    [ResponsibleType.RESPONSIBLE]?: ResponsibleNotificationStatus;
    [ResponsibleType.OWNER]?: ResponsibleNotificationStatus;
    [ResponsibleType.OTHER]?: ResponsibleNotificationStatus;
  };
}

type Responsible = {
  type?: ResponsibleType;
  name?: string;
  phone?: string;
  email?: string;
  notificationOptions?: {
    notifyOnReservationCreated: boolean;
    notifyOnReservationApproved: boolean;
    notifyOnReservationCanceled: boolean;
    notify48HoursBeforeCheckIn: boolean;
  };
  showNotificationOptions?: boolean;
};

export interface Residence {
  id?: number | null | undefined;
  inactive_at?: string;
  inactive_motive?: string;
  timeZone: TimeZone;
  names: NamesInterface;
  internalName: string;
  highlightImage?: string;
  highlightImageUrl?: string;
  intervals: IntervalInterface;
  partner_codes: PartnerCodesInterface;
  residenceInfo: residenceInfoInterface;
  hasGarage: OptionChoice,
  garagesCount: number,
  cep: string;
  street: string;
  number: string;
  complement?: string;
  neighborhood: string;
  city: string;
  state: string;
  lat?: string | number;
  lng?: string | number;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string;
  checkinModelId?: number;
  checkoutModelId?: number;
  important_info?: string;
  allowCheckInApprovalOnlyAllStepsChecked?: OptionChoice;
  usesDynamicPassword?: boolean;
  condoReleaseSystemId?: number | null;
  CondoReleaseSystem: CondoReleaseSystem;
  otherResponsibles?: Responsible[];
  hasPreferentialReservations?: boolean;
  usesTTLock?: boolean;
  usesTuyaLock?: boolean;
  ttlock_id?: string;
  requiresFacialRecognitionFromAdditionalGuests: boolean;
  cautionConfig: CautionConfig;
  petsAllowed: OptionChoice;
  maxPetsAllowed?: number;
  maxGuestsAllowed: number;
  checkinAndCheckoutTimeType: CheckinAndCheckoutTimeType;
  user?: IUser;
  maskCPFonPDF: OptionChoice;
}

export enum CheckinAndCheckoutTimeType {
  FIXED = "fixed", //At the beginning of the interval
  FLEXIBLE = "flexible", //Anytime within the interval
}

export interface Plan {
  id?: number;
  active?: "1" | "0" | null | undefined;
  name?: string;
  price?: number;
  gatewayReference?: string;
  trial_days?: number;
  minResidences: number;
  maxResidences: number;
  residencesCountOnThisPlan?: number;
  unlimitedPreview?: OptionChoice;
  residences?: Residence[];
  subscriptions?: Subscription[];
  limitations?: UserLimitations;
  basicPlan?: OptionChoice;
}

export enum SubscriptionStatus {
  /**
   * Assinatura ativa e em dia
   */
  ACTIVE = "active",
  /**
   * Assinatura expirada, cancelada ou com falhas no pagamento
   */
  INACTIVE = "inactive",
}

export interface Subscription {
  id?: number;
  status?: SubscriptionStatus;
  user_id?: number;
  plan_id?: number;
  startAt?: string;
  url: string;
  residencesCount: number;
  plan: Plan;
  created_at?: string;
  sourcesConfig?: SourcesConfig;
}

export interface Location {
  type?: string;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
}

export type LocationParams =
  | { city: string; cep?: never }
  | { cep: string; city?: never };

export interface DynamicField {
  name: {
    name_pt: string;
    name_en: string;
    name_es: string;
  };
  content: {
    pt: string;
    en: string;
    es: string;
  };
  value: boolean;
}

export interface InfoFormData {
  id?: string;
  residenceId?: string | number;
  dynamicFields: DynamicField[];
}

export type InfoFormDataGuide = Omit<
  InfoFormData,
  "dynamicBooleanFields" | "DynamicBooleanField"
>;

export interface NearbyPlaces {
  id: number;
  name: string;
  type: string;
  cep: string;
  street: string;
  number?: string;
  complement?: string;
  neighborhood: string;
  city: string;
  state: string;
  residenceId: number;
  lat?: string | number;
  lng?: string | number;
  position: number;
  phone?: string;
  websiteLink?: string;
  defaultContactPhrase?: Translations;
  image?: string;
}

export interface Translations {
  pt_BR?: string
  en_US?: string
  es_ES?: string
}

export interface Subsection {
  title: {
    name_pt: string;
    name_en: string;
    name_es: string;
  };
  locations?: Location[];
  description?: {
    pt: string;
    en: string;
    es: string;
  };
  visible?: boolean;
  dropdown?: boolean;
}
export interface SectionContent {
  title: string;
  subsections?: Subsection[];
}

export interface Section {
  name: string;
  key: string;
  content: SectionContent[];
}

interface NearbyPlace {
  id: string;
  name: string;
  type: string;
  cep: string;
  street: string;
}

interface Dynamic {
  title: string;
  description: string;
  visible: boolean;
  dropdown: boolean;
}

export interface Details {
  dynamicSection: Dynamic[];
  dynamicSectionGuide: Dynamic[];
  nearby: NearbyPlace[];
  residence: Residence;
}

export enum LangType {
  pt_BR = "pt_BR",
  en_US = "en_US",
  es_ES = "es_ES",
}

export enum TimeZone {
  BR_SAO_PAULO = "America/Sao_Paulo",       // Horário de Brasília (GMT-3, horário mais comum no Brasil)
  BR_MANAUS = "America/Manaus",            // Horário do Amazonas (GMT-4)
  BR_CUIABA = "America/Cuiaba",            // Horário do Mato Grosso (GMT-4)
  BR_FORTALEZA = "America/Fortaleza",      // Horário de Fortaleza (GMT-3, sem horário de verão)
  BR_RECIFE = "America/Recife",            // Horário de Recife (GMT-3, sem horário de verão)
  BR_BAHIA = "America/Bahia",              // Horário da Bahia (GMT-3, sem horário de verão)
  BR_BELEM = "America/Belem",              // Horário de Belém (GMT-3, sem horário de verão)
  BR_PORTO_VELHO = "America/Porto_Velho",   // Horário de Rondônia (GMT-4)
  BR_BOA_VISTA = "America/Boa_Vista",       // Horário de Roraima (GMT-4)
  BR_RIO_BRANCO = "America/Rio_Branco",     // Horário do Acre (GMT-5)
  BR_NORONHA = "America/Noronha"           // Horário de Fernando de Noronha (GMT-2)
}

export enum VisitMotive {
  LEISURE = "leisure",
  WORK = "work",
  SHOPPING = "shopping",
  FAMILY_FRIENDS = "family_friends",
  CONGRESS_FAIR = "congress_fair",
  COURSE = "course",
  HEALTH_TREATMENT = "health_treatment",
  RELIGION = "religion",
  OTHER = "other",
  DONT_INFORM = "dont_inform",
}

export enum VehicleType {
  CAR = 'car',
  MOTORCYCLE = 'motorcycle',
}

export interface VehicleData {
  type: VehicleType;
  licensePlate: string;
}

export type VehiclesDataInterface = VehicleData[];

export interface petsData {
    type: string;
}[];


export enum HomeCardMode {
  TODAY = 'today',
  TOMORROW = 'tomorrow',
  NEXT7DAYS = 'next7days',
  NEXT30DAYS = 'next30days',
}

interface StepModel {
  id: number;
  name: string;
  type: "checkin" | "checkout";
}

export enum ReservationsPeriodReport {
  PENDING = 'pending',
  FILLING = 'filling',
  WAITING_APPROVAL = 'waitingApproval',
  APPROVED = 'approved',
  CHECKOUT_FINISHED = 'checkoutFinished',
  REFUSED = 'refused',
  CANCELLED = 'cancelled',
  HIDDEN = 'hidden',
  TOTAL = 'total',
  PREFERENCIAL_RESERVATIONS = 'preferencialReservations'
}

export interface UserSocialMediaLinks {
  instagram?: string;
}

export enum StepsProgressStatus {
    NOT_STARTED = 'not_started',
    IN_PROGRESS = 'in_progress',
    COMPLETED = 'completed',
}

export enum OldReservationImportLookupField {
  CPF = "cpf",
  DOCUMENT = "document",
  SOURCE = "source",
}

export interface CautionDetails {
  hasCaution: OptionChoice;
  value: number;
  observation?: {
    [LangType.pt_BR]?: string;
    [LangType.en_US]?: string;
    [LangType.es_ES]?: string;
  };
}

export type CautionConfig = {
  [SourceKey in ReservationSource]: CautionDetails;
};

export interface SourcesDetails {
    sendNotifications: OptionChoice;
} 

export type SourcesConfig = {
    [SourceKey in ReservationSource]: SourcesDetails;
}

export enum ManualNotificationTypes {
    APPROVED = "approved",
    REFUSED = "refused",
    IMCOMPLETE = "incomplete",
}

export interface ContactsData {
    whatsapp?: {
        number: number,
        useOnForm?: OptionChoice,
        useOnCheckinAndCheckoutTimeInfo?: OptionChoice,
    };
}

export interface userFilterOptions {
    mainPage?: {
        hasLimitToListReservations?: OptionChoice,
        maxOverdueDaysVisible?: number,
    }
}